import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import { Box, Grid, Stack, styled } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { getCallLogTranscript, getJobById, IJob } from 'wisestep-api';
import { WsColors, WsTypography, WsTypoVariants, WsTypoColors } from 'wisestep-base';

import WsAvatar from '../../teams/components/WsAvatar';
import { CallDetails, ICommunicationLog, ITranscript } from '../types/CommunicationTypes';
import {
  defaultCommunicationLog,
  formatCallType,
  getCommStatusBadge,
} from '../utils/communicationUtils';

interface CallTranscriptProps {
  callLog: ICommunicationLog<CallDetails>;
  jobInfo?: IJob;
  isLoading?: boolean;
}

const ParticipantInfoContainer = styled(Box)(({ theme }) => ({
  backgroundColor: WsColors.TertiaryShades50,
  padding: theme.spacing(1.5, 3.5),
  borderRadius: theme.spacing(0.875),
  margin: theme.spacing(2, 2, 0, 2),
}));

const InfoRow = styled(Stack)(() => ({
  alignItems: 'center',
  gap: 9,
}));

const TranscriptContainer = styled(Box)(() => ({
  height: 562,
  padding: '16px 26px',
  backgroundColor: WsColors.White,
  overflowY: 'auto',
  position: 'relative',
}));

const TranscriptBackground = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundImage: `url("data:image/svg+xml,%3Csvg width='528' height='562' viewBox='0 0 528 562' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='528' height='562' fill='%23F6F6F9'/%3E%3C/svg%3E")`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  opacity: 0.3,
}));

const MessageBubble = styled(Box)<{ role: 'client' | 'recruiter' }>(({ role }) => ({
  maxWidth: '80%',
  padding: '8px 12px',
  marginBottom: 16,
  borderRadius: 8,
  backgroundColor: role === 'recruiter' ? WsColors.TheBlues100 : WsColors.White,
  border: `1px solid ${WsColors.TertiaryShades200}`,
  alignSelf: role === 'recruiter' ? 'flex-end' : 'flex-start',
  marginLeft: role === 'recruiter' ? 'auto' : 0,
  marginRight: role === 'client' ? 'auto' : 0,
}));

const SpeakerLabel = styled(Box)(() => ({
  marginBottom: 4,
  paddingLeft: 46,
}));

const InfoIcon = styled(Box)(() => ({
  width: 18,
  height: 18,
  color: WsColors.TertiaryShades800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const CallTranscript: FC<CallTranscriptProps> = ({ callLog, isLoading = false }) => {
  callLog = callLog ?? defaultCommunicationLog();
  const [transcript, setTranscript] = useState<ITranscript | null>(null);
  const [job, setJob] = useState<IJob | null>(null);

  useEffect(() => {
    if (!callLog.jobId) return;
    const fetchJobData = async () => {
      const jobData: IJob = (await getJobById(callLog.jobId)).data;
      jobData && setJob(jobData);
    };
    fetchJobData();
  }, [callLog.jobId]);

  useEffect(() => {
    const fetchCallTranscript = async () => {
      try {
        const response = await getCallLogTranscript(
          callLog.details.transcriptURL,
          '202aca62-4f81-4395-987a-312712b916fa'
        );
        setTranscript(response.data);
      } catch (error) {
        console.error('Error fetching transcript:', error);
      }
    };
    fetchCallTranscript();
  }, [callLog.details.transcriptURL]);

  return (
    <Grid container direction="column" sx={{ height: '100%' }}>
      <Grid item>
        <ParticipantInfoContainer>
          <Stack direction="row" alignItems="center" gap={1.25} sx={{ mb: 1.5 }}>
            <WsAvatar name={callLog.candidateName} size="small" variant="circular" />
            <Box flex={1}>
              <WsTypography
                variant={WsTypoVariants.BODY1}
                color={WsTypoColors.PRIMARY750}
                bold
                cursor="pointer"
              >
                {callLog.candidateName}
              </WsTypography>
            </Box>
            {getCommStatusBadge(callLog.status, callLog.type)}
          </Stack>
          <Stack direction="row" alignItems="center" gap={3}>
            <InfoRow>
              <InfoIcon>
                <PhoneIcon sx={{ fontSize: 18 }} />
              </InfoIcon>
              <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.SENARY}>
                {formatCallType(callLog.type)}
              </WsTypography>
            </InfoRow>
            {job?.companyName && (
              <InfoRow>
                <InfoIcon>
                  <BusinessIcon sx={{ fontSize: 18 }} />
                </InfoIcon>
                <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.PRIMARY750} bold>
                  {job.companyName}
                </WsTypography>
              </InfoRow>
            )}
          </Stack>
        </ParticipantInfoContainer>
      </Grid>
      <Grid item flex={1} sx={{ overflow: 'hidden' }}>
        <TranscriptContainer>
          <TranscriptBackground />
          <Stack direction="column" sx={{ position: 'relative', zIndex: 1 }}>
            {transcript?.timeline.map((message, index) => (
              <Box key={index}>
                <SpeakerLabel>
                  <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.BLACK}>
                    {message.users[0].username}
                  </WsTypography>
                </SpeakerLabel>
                <MessageBubble
                  role={isNaN(Number(message.users[0].username)) ? 'recruiter' : 'client'}
                >
                  <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.BLACK}>
                    {message.text}
                  </WsTypography>
                </MessageBubble>
              </Box>
            ))}
            {isLoading && (
              <Box display="flex" justifyContent="center" mt={2}>
                <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.QUATERNARY}>
                  Loading transcript...
                </WsTypography>
              </Box>
            )}
          </Stack>
        </TranscriptContainer>
      </Grid>
    </Grid>
  );
};

export default CallTranscript;
